import { getDatabase, ref, onValue, onDisconnect, set, serverTimestamp as databaseServerTimestamp, get, update, type DatabaseReference } from 'firebase/database';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import app, { db } from '../config/firebase';

export interface PresenceData {
  userId: string;
  isOnline: boolean;
  lastSeen: number;
  orgId?: string;
  currentPage?: string;
  displayName?: string;
  avatar?: string;
}

const ONLINE_WINDOW_MS = 5 * 60 * 1000;

function toMillis(value: unknown): number {
  if (typeof value === 'number') {
    return value < 10_000_000_000 ? value * 1000 : value;
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  if (value && typeof value === 'object') {
    const timestamp = value as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
    if (typeof timestamp.toMillis === 'function') {
      return timestamp.toMillis();
    }
    if (typeof timestamp.seconds === 'number') {
      return timestamp.seconds * 1000 + Math.floor((timestamp.nanoseconds || 0) / 1_000_000);
    }
  }
  return 0;
}

function isRecent(lastSeen: number): boolean {
  return lastSeen > Date.now() - ONLINE_WINDOW_MS;
}

function normalizePresence(raw: unknown): PresenceData | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  const userId = typeof data.userId === 'string' ? data.userId : '';
  if (!userId) return null;

  const lastSeen = toMillis(data.lastSeen);
  const isOnline = data.isOnline !== false && isRecent(lastSeen);

  return {
    userId,
    isOnline,
    lastSeen,
    orgId: typeof data.orgId === 'string' ? data.orgId : undefined,
    currentPage: typeof data.currentPage === 'string' ? data.currentPage : undefined,
    displayName: typeof data.displayName === 'string' ? data.displayName : undefined,
    avatar: typeof data.avatar === 'string' ? data.avatar : undefined,
  };
}

function presenceFromUserDoc(userId: string, data: Record<string, unknown>): PresenceData | null {
  const presence = data.presence && typeof data.presence === 'object'
    ? data.presence as Record<string, unknown>
    : {};

  return normalizePresence({
    userId,
    orgId: data.orgId,
    displayName: presence.displayName || data.name || data.email,
    avatar: presence.avatar || data.avatar,
    currentPage: presence.currentPage,
    isOnline: presence.isOnline,
    lastSeen: presence.lastSeen || data.lastSeen,
  });
}

class PresenceService {
  private database = getDatabase(app);
  private presenceRef: DatabaseReference | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private currentUserId: string | null = null;
  private currentOrgId: string | null = null;
  private currentDisplayName: string = 'User';
  private currentAvatar?: string;
  private currentPage?: string;

  /**
   * Start tracking user presence
   */
  startPresenceTracking(
    userId: string,
    orgId: string,
    displayName?: string,
    avatar?: string
  ): void {
    if (!userId || !orgId) return;

    if (this.presenceRef && this.currentUserId === userId && this.currentOrgId === orgId) {
      this.currentDisplayName = displayName || this.currentDisplayName;
      this.currentAvatar = avatar || this.currentAvatar;
      this.writePresence(true);
      return;
    }

    if (this.presenceRef && this.currentUserId && this.currentOrgId) {
      this.stopPresenceTracking(this.currentUserId, this.currentOrgId);
    }

    try {
      this.currentUserId = userId;
      this.currentOrgId = orgId;
      this.currentDisplayName = displayName || 'User';
      this.currentAvatar = avatar || undefined;

      // Reference to user's presence in their organization
      this.presenceRef = ref(this.database, `presence/${orgId}/${userId}`);

      this.writePresence(true);

      // When user disconnects, mark as offline
      onDisconnect(this.presenceRef).set({
        userId,
        isOnline: false,
        lastSeen: databaseServerTimestamp(),
        orgId,
        displayName: displayName || 'User',
        avatar: avatar || null,
      });

      // Update presence every 30 seconds to show activity
      this.heartbeatInterval = setInterval(() => {
        this.writePresence(true);
      }, 30000);

      console.log('✅ Presence tracking started for user:', userId);
    } catch (error) {
      console.error('❌ Error starting presence tracking:', error);
    }
  }

  /**
   * Stop tracking user presence
   */
  stopPresenceTracking(userId: string, orgId: string): void {
    try {
      this.writePresence(false);

      if (this.presenceRef) {
        // Set user as offline
        set(this.presenceRef, {
          userId,
          isOnline: false,
          lastSeen: databaseServerTimestamp(),
          orgId,
          displayName: this.currentDisplayName,
          avatar: this.currentAvatar || null,
        });

        this.presenceRef = null;
      }

      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }

      this.currentUserId = null;
      this.currentOrgId = null;
      this.currentPage = undefined;

      console.log('✅ Presence tracking stopped');
    } catch (error) {
      console.error('❌ Error stopping presence tracking:', error);
    }
  }

  /**
   * Listen to presence changes for all users in organization
   */
  onPresenceChange(
    orgId: string,
    callback: (users: PresenceData[]) => void
  ): () => void {
    let databasePresence: PresenceData[] = [];
    let firestorePresence: PresenceData[] = [];

    const emitMergedPresence = () => {
      const merged = new Map<string, PresenceData>();
      [...databasePresence, ...firestorePresence].forEach((presence) => {
        const normalized = normalizePresence(presence);
        if (!normalized) return;
        const existing = merged.get(normalized.userId);
        if (!existing || normalized.lastSeen >= existing.lastSeen) {
          merged.set(normalized.userId, normalized);
        }
      });
      callback(Array.from(merged.values()));
    };

    let unsubscribeDatabase = () => {};
    let unsubscribeFirestore = () => {};

    try {
      const presenceRef = ref(this.database, `presence/${orgId}`);

      unsubscribeDatabase = onValue(presenceRef, (snapshot) => {
        const data = snapshot.val();
        if (!data) {
          databasePresence = [];
          emitMergedPresence();
          return;
        }

        databasePresence = Object.values(data)
          .map(normalizePresence)
          .filter((item): item is PresenceData => Boolean(item));
        emitMergedPresence();
      }, (error) => {
        console.warn('Realtime Database presence unavailable, using Firestore fallback:', error);
        databasePresence = [];
        emitMergedPresence();
      });
    } catch (error) {
      console.warn('Realtime Database presence unavailable, using Firestore fallback:', error);
    }

    try {
      const usersQuery = query(collection(db, 'users'), where('orgId', '==', orgId));
      unsubscribeFirestore = onSnapshot(usersQuery, (snapshot) => {
        firestorePresence = snapshot.docs
          .map((userDoc) => presenceFromUserDoc(userDoc.id, userDoc.data()))
          .filter((item): item is PresenceData => Boolean(item));
        emitMergedPresence();
      }, (error) => {
        console.error('❌ Error listening to Firestore presence:', error);
        firestorePresence = [];
        emitMergedPresence();
      });
    } catch (error) {
      console.error('❌ Error listening to Firestore presence:', error);
    }

    return () => {
      unsubscribeDatabase();
      unsubscribeFirestore();
    };
  }

  /**
   * Get online users in organization
   */
  async getOnlineUsers(orgId: string): Promise<PresenceData[]> {
    try {
      const usersQuery = query(collection(db, 'users'), where('orgId', '==', orgId));
      const snapshot = await getDocs(usersQuery);
      const users = snapshot.docs
        .map((userDoc) => presenceFromUserDoc(userDoc.id, userDoc.data()))
        .filter((item): item is PresenceData => Boolean(item));

      return users.filter(user => user.isOnline);
    } catch (error) {
      console.warn('Firestore presence unavailable, trying Realtime Database:', error);
    }

    try {
      const presenceRef = ref(this.database, `presence/${orgId}`);
      const snapshot = await get(presenceRef);
      if (!snapshot.exists()) return [];

      const users = Object.values(snapshot.val())
        .map(normalizePresence)
        .filter((item): item is PresenceData => Boolean(item));
      return users.filter(user => user.isOnline);
    } catch (error) {
      console.error('❌ Error getting online users:', error);
      return [];
    }
  }

  /**
   * Get specific user's presence
   */
  async getUserPresence(
    userId: string,
    orgId: string
  ): Promise<PresenceData | null> {
    try {
      const userSnapshot = await getDoc(doc(db, 'users', userId));
      if (userSnapshot.exists()) {
        const presence = presenceFromUserDoc(userSnapshot.id, userSnapshot.data());
        if (presence) return presence;
      }
    } catch (error) {
      console.warn('Firestore presence unavailable, trying Realtime Database:', error);
    }

    try {
      const userPresenceRef = ref(this.database, `presence/${orgId}/${userId}`);
      const snapshot = await get(userPresenceRef);

      if (!snapshot.exists()) {
        return null;
      }

      return normalizePresence(snapshot.val());
    } catch (error) {
      console.error('❌ Error getting user presence:', error);
      return null;
    }
  }

  /**
   * Update current page for user (optional feature)
   */
  updateCurrentPage(userId: string, orgId: string, pageName: string): void {
    try {
      this.currentPage = pageName;
      if (this.presenceRef) {
        update(this.presenceRef, {
          userId,
          isOnline: true,
          orgId,
          lastSeen: databaseServerTimestamp(),
          currentPage: pageName,
        });
      }
      this.writePresence(true);
    } catch (error) {
      console.error('❌ Error updating current page:', error);
    }
  }

  /**
   * Check if user is online (last seen within 5 minutes)
   */
  isUserOnline(lastSeen: number): boolean {
    return isRecent(toMillis(lastSeen));
  }

  /**
   * Format last seen time for display
   */
  formatLastSeen(lastSeen: number): string {
    const normalizedLastSeen = toMillis(lastSeen);
    if (!normalizedLastSeen) return 'Noch keine Aktivität';

    const now = Date.now();
    const diff = now - normalizedLastSeen;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (this.isUserOnline(normalizedLastSeen)) {
      return 'Online';
    }

    if (minutes < 1) return 'Gerade eben';
    if (minutes === 1) return 'Vor 1 Minute';
    if (minutes < 60) return `Vor ${minutes} Minuten`;
    if (hours === 1) return 'Vor 1 Stunde';
    if (hours < 24) return `Vor ${hours} Stunden`;
    if (days === 1) return 'Vor 1 Tag';
    return `Vor ${days} Tagen`;
  }

  private writePresence(isOnline: boolean): void {
    if (!this.currentUserId || !this.currentOrgId) return;

    const firestorePresence = {
      userId: this.currentUserId,
      orgId: this.currentOrgId,
      isOnline,
      lastSeen: Date.now(),
      currentPage: this.currentPage || null,
      displayName: this.currentDisplayName,
      avatar: this.currentAvatar || null,
    };

    void setDoc(doc(db, 'users', this.currentUserId), {
      orgId: this.currentOrgId,
      presence: firestorePresence,
    }, { merge: true }).catch((error) => {
      console.error('❌ Error writing Firestore presence:', error);
    });

    if (!this.presenceRef) return;

    void set(this.presenceRef, {
      ...firestorePresence,
      lastSeen: databaseServerTimestamp(),
    }).catch((error) => {
      console.warn('Realtime Database presence write failed; Firestore fallback is active:', error);
    });
  }
}

export const presenceService = new PresenceService();
